import { useIntl } from 'react-intl';

import { type ButtonVariants } from '@xing-com/button';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useAppliedTheme } from '@xing-com/hub';
import {
  IconAddContact,
  IconBookmark,
  IconNotificationOn,
  IconLike,
  IconDislike,
  IconCross,
} from '@xing-com/icons';
import {
  xdlColorControlFillInvert,
  xdlPalettePurpleRain06,
  xdlColorControlFill,
  xdlPaletteRed05,
} from '@xing-com/tokens';

import { useJobCardActions, useMemberCardActions } from './actions/index';
import { CardData, CardType } from './types';

type ActionButtonType = {
  icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
  a11y: string;
  action: (deckIndex: number) => void;
  color: string;
  variant?: ButtonVariants;
  size?: 'small' | 'medium';
};

type useActionsReturnType = {
  [cardType in CardType]: {
    cardClickHandler?: (data: CardData, deckIndex: number) => void;
    buttons?: ActionButtonType[];
    handleSwipe: (
      deckIndex: number,
      action: 'left' | 'right' | 'super'
    ) => void;
  };
};

export const useActions = (): useActionsReturnType => {
  const appliedTheme = useAppliedTheme();
  const isDarkMode = appliedTheme === 'dark';
  const { availableCardClickHandler, takenCardClickHandler } =
    useJobCardActions();
  const intl = useIntl();
  const { isLoggedOut } = useLoginState();
  const {
    jobCardDislike,
    jobCardLike,
    jobCardBookmark,
    jobCardCreateAlert,
    jobCardSwiping,
  } = useJobCardActions();
  const { memberCardSendRequest, memberCardDecline, memberCardSwiping } =
    useMemberCardActions();

  const TakenJobLIButtons: ActionButtonType[] = [
    {
      icon: IconDislike,
      a11y: intl.formatMessage({
        id: 'DREAM_MACHINE_GAME_BUTTON_DISLIKE_CONTENT_DESCRIPTION_A11Y',
      }),
      action: jobCardDislike,
      color: xdlColorControlFill,
      variant: isDarkMode ? 'onColorStrong' : 'onColor',
      size: 'small',
    },
    {
      icon: IconNotificationOn,
      a11y: intl.formatMessage({
        id: 'SEARCH_AND_SHUFFLE_AVAILABLE_JOB_BUTTON_ALERT_A11Y',
      }),
      action: jobCardCreateAlert,
      color: xdlColorControlFillInvert,
      variant: isDarkMode ? 'onColor' : 'onColorStrong',
    },
    {
      icon: IconLike,
      a11y: intl.formatMessage({
        id: 'DREAM_MACHINE_GAME_BUTTON_LIKE_CONTENT_DESCRIPTION_A11Y',
      }),
      action: jobCardLike,
      color: xdlColorControlFill,
      variant: isDarkMode ? 'onColorStrong' : 'onColor',
      size: 'small',
    },
  ];

  const AvailableJobLIButtons: ActionButtonType[] = [
    {
      icon: IconDislike,
      a11y: intl.formatMessage({
        id: 'DREAM_MACHINE_GAME_BUTTON_DISLIKE_CONTENT_DESCRIPTION_A11Y',
      }),
      action: jobCardDislike,
      color: xdlColorControlFill,
      variant: isDarkMode ? 'onColorStrong' : 'onColor',
      size: 'small',
    },
    {
      icon: IconBookmark,
      a11y: intl.formatMessage({
        id: 'SEARCH_AND_SHUFFLE_TAKEN_JOB_BUTTON_BOOKMARK_A11Y',
      }),
      action: jobCardBookmark,
      color: xdlPalettePurpleRain06,
    },
    {
      icon: IconLike,
      a11y: intl.formatMessage({
        id: 'DREAM_MACHINE_GAME_BUTTON_LIKE_CONTENT_DESCRIPTION_A11Y',
      }),
      action: jobCardLike,
      color: xdlColorControlFill,
      variant: isDarkMode ? 'onColorStrong' : 'onColor',
      size: 'small',
    },
  ];

  const TakenJobLOButtons: ActionButtonType[] = [
    {
      icon: IconCross,
      a11y: intl.formatMessage({
        id: 'SEARCH_AND_SHUFFLE_BUTTON_DISCARD_A11Y',
      }),
      action: jobCardDislike,
      color: xdlPaletteRed05,
    },
    {
      icon: IconLike,
      a11y: intl.formatMessage({
        id: 'DREAM_MACHINE_GAME_BUTTON_LIKE_CONTENT_DESCRIPTION_A11Y',
      }),
      action: isLoggedOut ? jobCardBookmark : jobCardCreateAlert,
      color: xdlPalettePurpleRain06,
    },
  ];

  const AvailableJobLOButtons: ActionButtonType[] = [
    {
      icon: IconCross,
      a11y: intl.formatMessage({
        id: 'SEARCH_AND_SHUFFLE_BUTTON_DISCARD_A11Y',
      }),
      action: jobCardDislike,
      color: xdlPaletteRed05,
    },
    {
      icon: IconBookmark,
      a11y: intl.formatMessage({
        id: 'SEARCH_AND_SHUFFLE_TAKEN_JOB_BUTTON_BOOKMARK_A11Y',
      }),
      action: jobCardBookmark,
      color: xdlPalettePurpleRain06,
    },
  ];

  return {
    TakenJob: {
      cardClickHandler: takenCardClickHandler,
      handleSwipe: jobCardSwiping,
      buttons: isLoggedOut ? TakenJobLOButtons : TakenJobLIButtons,
    },
    AvailableJob: {
      cardClickHandler: availableCardClickHandler,
      handleSwipe: jobCardSwiping,
      buttons: isLoggedOut ? AvailableJobLOButtons : AvailableJobLIButtons,
    },
    Member: {
      handleSwipe: (deckIndex, action) => {
        if (action === 'left' || action === 'right') {
          memberCardSwiping(deckIndex, action);
        }
      },
      buttons: [
        {
          icon: IconCross,
          a11y: intl.formatMessage({
            id: 'NETWORK_SEARCH_AND_SWIPE_DISCARD_RECOMMENDATION_BUTTON_A11Y',
          }),
          action: memberCardDecline,
          color: xdlPaletteRed05,
        },
        {
          icon: IconAddContact,
          a11y: intl.formatMessage({
            id: 'NETWORK_SEARCH_AND_SWIPE_SEND_CONTACT_REQUEST_BUTTON_A11Y',
          }),
          action: memberCardSendRequest,
          color: xdlPalettePurpleRain06,
        },
      ],
    },
    Error: {
      handleSwipe: () => {},
    },
  };
};
